import * as THREE from 'three';
import '../ui/fortress.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, getFlag } from '../core/save.js';
import { addFound, hasFound } from '../core/collection.js';

// The Fortress interior — Toast Town's first RPG dungeon hall, reached from the
// Forest once quests swing its gate open. A torch-lit stone great-hall: flagstone
// floor, columns, banners, a caretaker to meet, and a treasure chest sealed with
// cold iron. Light all four braziers to break the seal and claim the Baker King's
// hoard. A sealed inner door hints at deeper floors (coming soon).
//
// Sibling of forestScene: explore state, proximity hotspots, tap-to-walk, an
// orbit-follow camera, and the shared .cafe-* result-panel UI for the reward.

export function createFortressScene(){
  return () => new FortressScene();
}

// fortress palette
const STONE   = 0x7C776C;
const STONE_D = 0x5E5A50;
const STONE_L = 0x928C7F;
const MORTAR  = 0x46433B;
const WOOD    = 0x5A3A22;
const GOLD    = 0xE8C24B;
const FLAME   = 0xFF9A3C;
const EMBER   = 0xFFD27A;
const BANNER1 = 0x9A2C46;   // deep raspberry
const BANNER2 = 0x2E479A;   // cobalt

const CHEST_POS = new THREE.Vector3(0, 0, -12.5);

class FortressScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.vignette = null;
    this.litCount = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x171018);
    this.scene.fog = new THREE.Fog(0x171018, 16, 40);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 13;
    this.camTarget = new THREE.Vector3(0, 1.6, 2);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildHall();
    this._buildBanners();
    this._buildColumns();
    this.braziers = this._buildBraziers();
    this.sconces  = this._buildSconces();
    this._buildChest();
    this._buildInnerDoor();
    this._buildCryptDoor();
    this._buildLibraryDoor();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 6.5);
    this.bread.root.rotation.y = Math.PI;                 // facing down the hall
    this.scene.add(this.bread.root);

    // Sir Loaf-a-Lot — the fortress's sleepy suit-of-armor caretaker.
    this.knight = this._makeKnight();
    this.scene.add(this.knight.root);

    this.hotspots = [
      { pos: this.knight.root.position.clone(), r: 2.6, label: '🗣 talk to Sir Loaf-a-Lot', action: () => this._talkToKnight() },
      ...this.braziers.map((b, i) => ({
        pos: new THREE.Vector3(b.x, 0, b.z + 1.3), r: 1.9,
        label: '🔥 light this brazier', action: () => this._lightBrazier(i)
      })),
      { pos: new THREE.Vector3(CHEST_POS.x, 0, CHEST_POS.z + 2.2), r: 2.0, label: '🗝 open the treasure chest', action: () => this._openChest() },
      { pos: new THREE.Vector3(-2.8, 0, -10.6), r: 2.0, label: '🚪 enter the dungeon', action: () => this._innerDoor() },
      { pos: new THREE.Vector3(2.8, 0, -10.6), r: 2.0, label: getFlag('fortressDungeonCleared') ? '💀 enter the crypt' : '🔒 crypt sealed', action: () => this._enterCrypt() },
      { pos: new THREE.Vector3(6.2, 0, -8), r: 2.2, label: '📚 enter the crust archives', action: () => this._enterLibrary() },
      { pos: new THREE.Vector3(0, 0, 7.4), r: 2.4, label: '← leave the Fortress', action: () => this.manager.goTo('forest', { from: 'fortress' }) }
    ];
    this.nearSpot = null;
  }

  // ---- build ----
  _buildLights(){
    // brighter cold base so the hall is readable even before braziers are lit
    this.scene.add(new THREE.AmbientLight(0x554a55, 0.75));
    const moon = new THREE.DirectionalLight(0x8fa0c8, 0.6);
    moon.position.set(4, 14, 8); moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    const s = 18;
    moon.shadow.camera.left = -s; moon.shadow.camera.right = s;
    moon.shadow.camera.top = s; moon.shadow.camera.bottom = -s;
    this.scene.add(moon);
    this.scene.fog = new THREE.Fog(0x171018, 16, 54);
  }

  _buildHall(){
    const HALF_W = 7, Z0 = -15, Z1 = 8.5;
    const len = Z1 - Z0, midZ = (Z0 + Z1) / 2;

    // flagstone floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, len), new THREE.MeshLambertMaterial({ color: STONE }));
    floor.rotation.x = -Math.PI/2; floor.position.set(0, 0, midZ); floor.receiveShadow = true;
    this.scene.add(floor);
    // a mortar grid of shallow inlay strips for flagstone feel
    for (let x = -HALF_W + 2; x < HALF_W; x += 2){
      const line = box(0.08, 0.02, len, MORTAR, x, 0.02, midZ, this.scene);
      line.castShadow = false;
    }
    for (let z = Z0 + 2; z < Z1; z += 2){
      const line = box(HALF_W * 2, 0.02, 0.08, MORTAR, 0, 0.02, z, this.scene);
      line.castShadow = false;
    }
    // a royal runner leading to the chest
    const runner = new THREE.Mesh(new THREE.PlaneGeometry(2.2, len - 3), new THREE.MeshLambertMaterial({ color: BANNER1 }));
    runner.rotation.x = -Math.PI/2; runner.position.set(0, 0.03, midZ - 1); this.scene.add(runner);

    // side walls
    const wallMat = new THREE.MeshLambertMaterial({ color: STONE_D });
    wallMat.transparent = true;
    const wallGeo = new THREE.PlaneGeometry(len, 8);
    this.wallL = new THREE.Mesh(wallGeo, wallMat.clone());
    this.wallL.rotation.y = Math.PI/2; this.wallL.position.set(-HALF_W, 4, midZ); this.wallL.receiveShadow = true;
    this.scene.add(this.wallL);
    this.wallR = new THREE.Mesh(wallGeo, wallMat.clone());
    this.wallR.rotation.y = -Math.PI/2; this.wallR.position.set(HALF_W, 4, midZ); this.wallR.receiveShadow = true;
    this.scene.add(this.wallR);

    // back wall (behind the chest)
    this.backWall = box(HALF_W * 2, 8, 0.4, STONE_D, 0, 4, Z0, this.scene);
    this.backWall.receiveShadow = true;
    this.backWall.material.transparent = true;
    // stone block trim near the floor + a heavy cornice
    this.backTrimA = box(HALF_W * 2, 0.6, 0.5, STONE, 0, 0.3, Z0 + 0.1, this.scene);
    this.backTrimA.material.transparent = true;
    this.backTrimB = box(HALF_W * 2 + 0.4, 0.5, 0.5, STONE, 0, 7.4, Z0 + 0.1, this.scene);
    this.backTrimB.material.transparent = true;

    // entrance wall at the near end, with a doorway gap back to the Forest
    const seg = (HALF_W * 2 - 2.4) / 2;
    this.entranceWallL = box(seg, 8, 0.4, STONE_D, -(1.2 + seg/2), 4, Z1, this.scene);
    this.entranceWallL.material.transparent = true;
    this.entranceWallR = box(seg, 8, 0.4, STONE_D,  (1.2 + seg/2), 4, Z1, this.scene);
    this.entranceWallR.material.transparent = true;
    this.lintel = box(2.6, 2.4, 0.5, STONE, 0, 6.6, Z1, this.scene);        // lintel over the doorway
    this.lintel.material.transparent = true;
    box(2.4, 3.4, 0.2, 0x0C0A10, 0, 1.7, Z1 - 0.24, this.scene); // dark opening beyond
    const exitSign = textSprite('← to the Forest', 26, '#EFE6D0', 2.6, 0.7);
    exitSign.position.set(0, 4.4, Z1 - 0.3); this.scene.add(exitSign);

    // ceiling — a dark vault to keep it feeling enclosed
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, len), new THREE.MeshLambertMaterial({ color: 0x211a24 }));
    ceil.rotation.x = Math.PI/2; ceil.position.set(0, 8, midZ); this.scene.add(ceil);

    // hall title over the back wall
    const sign = textSprite('The Fortress', 30, '#EFE6D0', 4.6, 1.0);
    sign.position.set(0, 6.4, Z0 + 0.4); this.scene.add(sign);
  }

  _buildBanners(){
    // hanging banners alternating on the side walls
    const zs = [4, 0, -4, -8];
    zs.forEach((z, i) => {
      const col = i % 2 ? BANNER2 : BANNER1;
      [-6.7, 6.7].forEach(x => {
        const b = box(1.4, 3.4, 0.08, col, x, 5.1, z, this.scene);
        b.rotation.y = x < 0 ? Math.PI/2 : -Math.PI/2;
        const crest = box(0.7, 0.7, 0.06, GOLD, x + (x < 0 ? 0.06 : -0.06), 5.0, z, this.scene);
        crest.rotation.y = x < 0 ? Math.PI/2 : -Math.PI/2;
      });
    });
  }

  _buildColumns(){
    const zs = [3, -1, -5, -9];
    zs.forEach(z => {
      [-5.4, 5.4].forEach(x => {
        box(1.1, 0.4, 1.1, STONE_L, x, 0.2, z, this.scene);        // base
        cyl(0.42, 0.5, 6.4, STONE, x, 3.4, z, this.scene, 16);      // shaft
        box(1.1, 0.5, 1.1, STONE_L, x, 6.8, z, this.scene);         // capital
      });
    });
  }

  // A brazier the player lights: a stone bowl on a stand with a hidden flame +
  // point light that switch on and flicker once lit.
  _buildBraziers(){
    const spots = [
      { x: -4.2, z: 2 }, { x: 4.2, z: 2 },
      { x: -4.2, z: -6 }, { x: 4.2, z: -6 }
    ];
    return spots.map(s => {
      const g = new THREE.Group(); g.position.set(s.x, 0, s.z); this.scene.add(g);
      cyl(0.14, 0.22, 1.5, STONE_D, 0, 0.75, 0, g, 10);            // stand
      cyl(0.55, 0.3, 0.45, STONE, 0, 1.6, 0, g, 14);               // bowl
      cyl(0.5, 0.5, 0.08, STONE_L, 0, 1.82, 0, g, 14);             // rim

      const flame = new THREE.Group(); flame.position.set(0, 1.9, 0); flame.visible = false; g.add(flame);
      this._cone(0.36, 0.9, FLAME, 0, 0.35, 0, flame);
      this._cone(0.2, 0.6, EMBER, 0, 0.55, 0, flame);

      const light = new THREE.PointLight(0xFF8A38, 0, 13, 2);
      light.position.set(s.x, 2.4, s.z); this.scene.add(light);

      return { group: g, flame, light, lit: false, x: s.x, z: s.z };
    });
  }

  // Always-on wall sconces for a base torch glow so the hall reads before the
  // braziers are lit.
  _buildSconces(){
    const specs = [
      { x: -6.8, z: 6 }, { x: 6.8, z: 6 },
      { x: -6.8, z: -12 }, { x: 6.8, z: -12 }
    ];
    return specs.map(s => {
      const g = new THREE.Group(); g.position.set(s.x, 3.6, s.z); this.scene.add(g);
      const bracket = box(0.18, 0.5, 0.18, WOOD, s.x < 0 ? 0.2 : -0.2, 0, 0, g);
      const flame = new THREE.Group(); flame.position.set(s.x < 0 ? 0.5 : -0.5, 0.35, 0); g.add(flame);
      this._cone(0.16, 0.45, FLAME, 0, 0.2, 0, flame);
      this._cone(0.09, 0.28, EMBER, 0, 0.32, 0, flame);
      const light = new THREE.PointLight(0xFF9A48, 0.7, 8, 2);
      light.position.set(s.x < 0 ? s.x + 0.6 : s.x - 0.6, 4.0, s.z); this.scene.add(light);
      return { flame, light, phase: Math.random() * 6 };
    });
  }

  _cone(r, h, color, x, y, z, parent){
    const m = new THREE.Mesh(new THREE.ConeGeometry(r, h, 9), new THREE.MeshBasicMaterial({ color }));
    m.position.set(x, y, z); parent.add(m); return m;
  }

  _buildChest(){
    const g = new THREE.Group(); g.position.copy(CHEST_POS); this.scene.add(g);
    box(1.9, 1.0, 1.2, WOOD, 0, 0.5, 0, g);                       // body
    box(2.0, 0.16, 1.3, GOLD, 0, 0.5, 0, g);                      // band
    box(0.16, 1.0, 1.3, GOLD, -0.85, 0.5, 0, g);
    box(0.16, 1.0, 1.3, GOLD,  0.85, 0.5, 0, g);

    // lid pivots on its back top edge so it swings open
    const lid = new THREE.Group(); lid.position.set(0, 1.0, -0.6); g.add(lid);
    box(1.9, 0.5, 1.2, WOOD, 0, 0.25, 0.6, lid);
    box(2.0, 0.14, 1.3, GOLD, 0, 0.42, 0.6, lid);
    this.chestLid = lid;

    this.chestLock = box(0.28, 0.34, 0.14, GOLD, 0, 0.55, 0.62, g);  // front lock
    // treasure glow that rises when opened
    this.crownReward = sphere(0.24, GOLD, 0, 0.6, 0, g, 12);
    this.crownReward.material = new THREE.MeshBasicMaterial({ color: EMBER });
    this.crownReward.visible = false;

    this.chest = { group: g, open: false, lidAngle: 0 };
  }

  _buildInnerDoor(){
    const g = new THREE.Group(); g.position.set(0, 0, -14.7); this.scene.add(g);
    box(2.6, 4.2, 0.3, WOOD, 0, 2.1, 0, g);                       // heavy door
    box(0.3, 4.2, 0.4, STONE_L, -1.5, 2.1, 0, g);                 // stone jambs
    box(0.3, 4.2, 0.4, STONE_L, 1.5, 2.1, 0, g);
    box(3.3, 0.5, 0.4, STONE_L, 0, 4.4, 0, g);                    // arch
    cyl(0.14, 0.14, 0.3, 0x1A1A1A, 0.7, 2.1, 0.2, g, 10).rotation.x = Math.PI/2;  // iron ring handle
    const sign = textSprite('Dungeon', 22, '#C9B98F', 2.0, 0.7);
    sign.position.set(0, 4.9, 0.3); g.add(sign);
  }

  _buildCryptDoor(){
    const g = new THREE.Group(); g.position.set(2.8, 0, -14.7); this.scene.add(g);
    box(1.6, 3.6, 0.3, 0x3A2E2E, 0, 1.8, 0, g);
    box(0.22, 3.6, 0.38, STONE_L, -1.0, 1.8, 0, g);
    box(0.22, 3.6, 0.38, STONE_L,  1.0, 1.8, 0, g);
    box(2.2, 0.4, 0.38, STONE_L, 0, 3.8, 0, g);
    // skull motif
    sphere(0.18, 0xE8E0D0, 0, 2.3, 0.2, g, 10);
    const sign = textSprite('Crypt', 20, '#C9B98F', 1.6, 0.6);
    sign.position.set(0, 4.2, 0.3); g.add(sign);
  }

  _makeKnight(){
    const STEEL = 0xB9C0C8, STEEL_D = 0x8A929B;
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.62, 1.1);                 // a loaf inside the armor
      // sleepy eyes
      const e1 = box(0.16, 0.035, 0.03, 0x2E2018, -0.17, 1.16, 0.32, g); e1.rotation.z = 0.25;
      const e2 = box(0.16, 0.035, 0.03, 0x2E2018,  0.17, 1.16, 0.32, g); e2.rotation.z = -0.25;
      box(0.4, 0.5, 0.24, STEEL, 0, 0.78, 0.02, g);               // breastplate
      box(0.5, 0.16, 0.26, STEEL_D, 0, 1.02, 0.02, g);            // collar gorget
      box(0.18, 0.12, 0.12, STEEL, -0.3, 0.86, 0.06, g);          // pauldrons
      box(0.18, 0.12, 0.12, STEEL,  0.3, 0.86, 0.06, g);
      // a helmet, visor up (sleepy)
      const helm = new THREE.Group(); helm.position.set(0, 1.7, 0); g.add(helm);
      cyl(0.34, 0.36, 0.36, STEEL, 0, 0.1, 0, helm, 14);
      sphere(0.34, STEEL, 0, 0.28, 0, helm, 14);
      box(0.14, 0.28, 0.1, STEEL_D, 0, 0.55, 0, helm);            // plume holder
      box(0.16, 0.4, 0.16, BANNER1, 0, 0.85, 0, helm);            // red plume
      // leaning on a tall shield
      const shield = box(0.7, 1.3, 0.12, STEEL_D, 0.62, 0.65, 0.1, g);
      shield.rotation.z = 0.12;
      box(0.5, 0.16, 0.14, GOLD, 0.62, 0.65, 0.16, g).rotation.z = 0.12;
      stickLegs(g, 0.5);
    };
    return new Townsperson({
      name: 'Sir Loaf-a-Lot',
      line: 'ho there… *yawn* …welcome to the keep. mind the braziers.',
      pos: new THREE.Vector3(3, 0, -3.2), facing: -Math.PI * 0.55,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.7];
    this.vignette = document.createElement('div');
    this.vignette.className = 'fortress-vignette';
    document.body.appendChild(this.vignette);
    this._positionCamera();
    this.bread.setLantern(hasFound('ghost-lamp') ? 1.0 : 0);
    this._toExplore(true);
  }

  exit(){
    this._clearPanels();
    if (this.vignette){ this.vignette.remove(); this.vignette = null; }
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _positionCamera(){
    this.azimuth = this.input?.azimuthGoal ?? 0;
    this.polar   = this.input?.polarGoal ?? 0.5;
    this.camTarget.set(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('the fortress');
    if (first) HUD.say('Inside the Fortress at last! A cold stone hall — a sleepy caretaker keeps it. Light all four braziers to break the seal on the treasure chest at the far end.');
    HUD.setHint('click to walk · light the four braziers, then open the chest · or head back to the Forest');
    HUD.hideHintSoon(9000);
  }

  _tap(ray){
    if (checkNpcClick(ray, this.knight, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6.2, Math.min(6.2, gp.x));
    gp.z = Math.max(-11.2, Math.min(7.4, gp.z));         // stay inside the hall
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  // ---- panels (reuse the shared .cafe-* game UI) ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- interactions ----
  _talkToKnight(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.knight.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    if (this.litCount >= this.braziers.length){
      HUD.say('"You lit them all! The old chest\'s seal is broken — the Baker King\'s hoard is yours, brave loaf." *snores*');
    } else {
      HUD.say(`"${this.braziers.length - this.litCount} braziers still cold. Warm them all and the treasure seal breaks. Deeper floors? Not yet, friend." *yawn*`);
    }
  }

  _lightBrazier(i){
    const b = this.braziers[i];
    this.bread.drawFace('effort');
    if (b.lit){ HUD.say('This brazier already crackles merrily.'); return; }
    b.lit = true;
    b.flame.visible = true;
    this.litCount++;
    HUD.refreshCrumbs();
    if (this.litCount >= this.braziers.length){
      this.chestLock.material = new THREE.MeshBasicMaterial({ color: EMBER });   // seal glows broken
      this.bread.drawFace('yum');
      HUD.say('The last brazier roars to life — with a deep CLUNK the chest\'s iron seal snaps open!');
    } else {
      HUD.say(`Whoomph! Brazier lit. (${this.litCount} / ${this.braziers.length}) — the hall grows warmer.`);
    }
  }

  _openChest(){
    if (this.litCount < this.braziers.length){
      this.bread.drawFace('o');
      HUD.say(`The chest is bound by cold iron. Light all ${this.braziers.length} braziers to break the seal! (${this.litCount} / ${this.braziers.length} lit)`);
      return;
    }
    this.chest.open = true;
    this.crownReward.visible = true;
    this.bread.drawFace('yum');

    const CRUMBS = 150;
    addCrumbs(CRUMBS); HUD.refreshCrumbs();
    const firstTime = !hasFound('relic-crown');
    addFound('relic-crown');

    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">🎉</div>
        <h2>Treasure!</h2>
        <div class="cafe-sub">the Baker King's hoard</div>
        <p class="fortress-loot">👑 Baker King's Crown</p>
        <p class="fortress-note">${firstTime ? 'a legendary relic — added to your collection!' : 'you already have this relic, but it still gleams.'}</p>
        <p class="cafe-payout">🍞 +${CRUMBS} crumbs</p>
        <p class="fortress-note">see it on display later in the Museum.</p>
        <button class="cafe-btn ghost close">huzzah!</button>
      </div>`);
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _innerDoor(){
    this.bread.stop();
    if (!hasFound('ghost-lamp')){
      HUD.say('The dungeon passage is pitch black. You need a light to go deeper.');
      return;
    }
    const allBraziers = this.braziers.every(b => b.lit);
    if (!allBraziers){
      HUD.say('A cold seal bars the door. Light every brazier to melt the iron.');
      return;
    }
    this.manager.goTo('fortressDungeon', { from: 'fortress' });
  }

  _enterCrypt(){
    this.bread.stop();
    if (!getFlag('fortressDungeonCleared')){
      HUD.say('A heavy seal of dough wards the crypt. Clear the dungeon above first.');
      return;
    }
    this.manager.goTo('fortressCrypt', { from: 'fortress' });
  }

  _enterLibrary(){
    this.bread.stop();
    this.manager.goTo('fortressLibrary', { from: 'fortress' });
  }

  _buildLibraryDoor(){
    // side archway to the Library
    const x = 6.4, z = -8;
    box(0.4, 4.2, 0.35, STONE_L, x - 1.1, 2.1, z, this.scene);
    box(0.4, 4.2, 0.35, STONE_L, x + 1.1, 2.1, z, this.scene);
    box(2.6, 0.5, 0.4, STONE_L, x, 4.4, z, this.scene);
    const sign = textSprite('Crust Archives ▸', 22, '#C9B98F', 2.8, 0.7);
    sign.position.set(x, 3.3, z); this.scene.add(sign);
  }

  // ---- loop ----
  update(dt){
    const t = performance.now() * 0.001;

    // flicker lit braziers
    this.braziers.forEach((b, i) => {
      if (!b.lit) return;
      b.light.intensity = 1.5 + Math.sin(t * 13 + i * 2.1) * 0.28 + Math.sin(t * 27 + i) * 0.12;
      b.flame.scale.y = 0.85 + 0.22 * Math.sin(t * 18 + i * 3);
      b.flame.scale.x = b.flame.scale.z = 0.92 + 0.1 * Math.sin(t * 22 + i);
    });
    // flicker always-on sconces
    this.sconces.forEach((s, i) => {
      s.light.intensity = 0.6 + 0.18 * Math.sin(t * 15 + s.phase) + 0.08 * Math.sin(t * 31 + i);
      s.flame.scale.y = 0.9 + 0.18 * Math.sin(t * 20 + s.phase);
    });

    // ease the chest lid open + float the crown
    const goal = this.chest.open ? -2.1 : 0;
    this.chest.lidAngle += (goal - this.chest.lidAngle) * Math.min(1, dt * 6);
    this.chestLid.rotation.x = this.chest.lidAngle;
    if (this.crownReward.visible){
      this.crownReward.position.y = 0.9 + Math.sin(t * 2) * 0.12;
      this.crownReward.rotation.y += dt * 1.5;
    }

    this.knight.update(dt, this.bread.root.position);
    this.bread.update(dt);

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => {
            if (near.action) near.action();
            else { HUD.say(near.say); this.bread.drawFace('o'); }
          });
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    // orbit-follow camera
    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
    this._fadeWalls();
  }

  _fadeWalls(){
    const HALF_W = 7, Z0 = -15, Z1 = 8.5;
    const cam = this.camera.position;
    const fade = (mesh, behind) => {
      if (!mesh) return;
      const target = behind ? 0.18 : 1;
      mesh.material.opacity += (target - mesh.material.opacity) * 0.12;
      mesh.visible = mesh.material.opacity > 0.02;
    };
    fade(this.wallL, cam.x < -HALF_W + 0.5);
    fade(this.wallR, cam.x >  HALF_W - 0.5);
    fade(this.backWall, cam.z < Z0 + 1.5);
    fade(this.backTrimA, cam.z < Z0 + 1.5);
    fade(this.backTrimB, cam.z < Z0 + 1.5);
    fade(this.entranceWallL, cam.z > Z1 - 1.0);
    fade(this.entranceWallR, cam.z > Z1 - 1.0);
    fade(this.lintel, cam.z > Z1 - 1.0);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
